/**
 * Classroom data access.
 *
 * The analytics live in `@yuan-math/learning`; this only fetches. Like
 * `storage.ts`, it is the single place that talks to Supabase for this feature,
 * and it fails softly — a teacher with no network sees an empty class, not a
 * broken page.
 */

import type { Assignment, AssignmentTarget, ClassMember, LearnerProfile } from '@yuan-math/learning';
import { supabaseClient } from './auth.ts';

export type TeacherClass = {
  id: string;
  name: string;
  focusConceptIds: string[];
  joinCode: string;
  studentCount: number;
};

export async function fetchClasses(teacherId: string): Promise<TeacherClass[]> {
  const client = supabaseClient();
  if (!client) return [];

  try {
    const { data, error } = await client
      .from('classes')
      .select('id, name, focus_concept_ids, join_code, class_members(count)')
      .eq('teacher_id', teacherId)
      .eq('archived', false)
      .order('created_at', { ascending: false });

    if (error || !data) return [];
    return data.map((row) => ({
      id: String(row.id),
      name: String(row.name),
      focusConceptIds: (row.focus_concept_ids as string[]) ?? [],
      joinCode: String(row.join_code),
      studentCount: (row.class_members as { count: number }[] | null)?.[0]?.count ?? 0,
    }));
  } catch {
    return [];
  }
}

/**
 * Loads every student's progress for one class.
 *
 * The row-level security policy scopes this to the caller's own classes, so a
 * teacher cannot read another teacher's students even by guessing an id.
 */
export async function fetchRoster(classId: string): Promise<ClassMember[]> {
  const client = supabaseClient();
  if (!client) return [];

  try {
    const { data, error } = await client
      .from('class_members')
      .select('account_id, learner_profiles!inner(id, display_name, data)')
      .eq('class_id', classId);

    if (error || !data) return [];

    return data
      .map((row) => {
        // PostgREST types an embedded relation as an array, but returns a bare
        // object for a to-one join. Accept either rather than guessing.
        const embedded = (row as Record<string, unknown>).learner_profiles;
        const profileRow = (Array.isArray(embedded) ? embedded[0] : embedded) as
          | { id: string; display_name: string; data: LearnerProfile }
          | undefined;

        if (!profileRow?.data?.id) return null;
        return {
          profileId: profileRow.id,
          displayName: profileRow.display_name,
          profile: profileRow.data,
        } satisfies ClassMember;
      })
      .filter((entry): entry is ClassMember => entry !== null);
  } catch {
    return [];
  }
}

export async function createClass(
  teacherId: string,
  name: string,
  focusConceptIds: string[],
): Promise<TeacherClass | null> {
  const client = supabaseClient();
  if (!client) return null;

  // Six characters, no vowels and no lookalikes: a code read aloud to a class
  // must not turn 0 into O or 1 into I.
  const alphabet = 'BCDFGHJKLMNPQRSTVWXZ23456789';
  const joinCode = Array.from({ length: 6 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');

  try {
    const { data, error } = await client
      .from('classes')
      .insert({ teacher_id: teacherId, name, focus_concept_ids: focusConceptIds, join_code: joinCode })
      .select()
      .single();

    if (error || !data) return null;
    return {
      id: String(data.id),
      name: String(data.name),
      focusConceptIds: (data.focus_concept_ids as string[]) ?? [],
      joinCode: String(data.join_code),
      studentCount: 0,
    };
  } catch {
    return null;
  }
}

export async function joinClass(code: string): Promise<{ name: string } | null> {
  const client = supabaseClient();
  if (!client) return null;
  try {
    const { data, error } = await client.rpc('join_class', { code });
    if (error || !data) return null;
    return { name: String((data as { name: string }).name) };
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------------- */
/* Assignments                                                                */
/* -------------------------------------------------------------------------- */

/** Row shape as stored; `Assignment` is the application shape. */
type AssignmentRow = {
  id: string;
  class_id: string;
  title: string;
  note: string | null;
  concept_ids: string[];
  target: AssignmentTarget;
  due_at: string | null;
  created_at: string;
};

const toAssignment = (row: AssignmentRow): Assignment => ({
  id: String(row.id),
  classId: String(row.class_id),
  title: String(row.title),
  ...(row.note ? { note: row.note } : {}),
  conceptIds: row.concept_ids ?? [],
  target: row.target ?? { kind: 'mastery' },
  ...(row.due_at ? { dueAt: Date.parse(row.due_at) } : {}),
  createdAt: Date.parse(row.created_at),
});

export async function fetchAssignments(classId: string): Promise<Assignment[]> {
  const client = supabaseClient();
  if (!client) return [];
  try {
    const { data, error } = await client
      .from('assignments')
      .select('*')
      .eq('class_id', classId)
      .order('created_at', { ascending: false });
    if (error || !data) return [];
    return (data as AssignmentRow[]).map(toAssignment);
  } catch {
    return [];
  }
}

/** Every assignment across every class the student belongs to. */
export async function fetchStudentAssignments(): Promise<Assignment[]> {
  const client = supabaseClient();
  if (!client) return [];
  try {
    // RLS scopes this to the caller's own classes, so no filter is needed —
    // and adding one would be a second place to get the security wrong.
    const { data, error } = await client.from('assignments').select('*').order('due_at', { ascending: true });
    if (error || !data) return [];
    return (data as AssignmentRow[]).map(toAssignment);
  } catch {
    return [];
  }
}

export async function createAssignment(input: {
  classId: string;
  title: string;
  note?: string;
  conceptIds: string[];
  target: AssignmentTarget;
  dueAt?: number;
}): Promise<Assignment | null> {
  const client = supabaseClient();
  if (!client) return null;
  try {
    const { data, error } = await client
      .from('assignments')
      .insert({
        class_id: input.classId,
        title: input.title,
        note: input.note ?? null,
        concept_ids: input.conceptIds,
        target: input.target,
        due_at: input.dueAt ? new Date(input.dueAt).toISOString() : null,
      })
      .select()
      .single();
    if (error || !data) return null;
    return toAssignment(data as AssignmentRow);
  } catch {
    return null;
  }
}

export async function deleteAssignment(id: string): Promise<boolean> {
  const client = supabaseClient();
  if (!client) return false;
  try {
    const { error } = await client.from('assignments').delete().eq('id', id);
    return !error;
  } catch {
    return false;
  }
}
